import type { TargetAndTransition } from "framer-motion";
import { motion, useReducedMotion } from "framer-motion";
import React from "react";
import type { AnimationType } from "../../theme/animations";
import { getAnimation } from "../../theme/animations";

interface Props {
  /** The type of animation to use */
  animation?: AnimationType;
  /** Adds an opacity when tapping */
  useTapEffect?: boolean;
  /**
   * Callback that fires when the item has finished animating.
   * It is also triggered if `useTapEffect=true` and tap the item.
   */
  onAnimationComplete?: ({ isVisible }: { isVisible: boolean }) => void;
  children?: React.ReactNode;
}

export const AnimatedItem = ({
  children,
  useTapEffect = false,
  animation = "fade-in",
  onAnimationComplete,
}: Props): React.JSX.Element => {
  const prefersReducedMotion = useReducedMotion();
  const { animate, initial, exit } = prefersReducedMotion
    ? getAnimation("reduced-motion")
    : getAnimation(animation);

  const onAnimationCompleteFunc = (target: TargetAndTransition): void => {
    const isVisible = target.opacity === 1;
    onAnimationComplete?.({ isVisible });
  };

  return (
    <motion.div
      animate={animate}
      initial={initial}
      exit={exit}
      onAnimationComplete={onAnimationCompleteFunc}
      whileTap={useTapEffect
        ? { opacity: 0.6, transition: { duration: 0.1 } }
        : undefined}
    >
      {children}
    </motion.div>
  );
};
